import React, { useState, useEffect } from 'react';
import {
  FaSearch, FaArrowLeft,
  FaCheckCircle, FaTimesCircle, FaSortAmountDown,
  FaUser, FaPhone, FaDownload, FaShare, FaSpinner, FaCoins, FaList, FaSync
} from 'react-icons/fa';
import { MdSend } from 'react-icons/md';
import './design.css';

import { ecardApi } from '../../api/eCards';
import { paymentApi } from '../../api/payments';
import PaymentModal from '../../modals/payments/PaymentModal/PaymentModal';
import { useAuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { handleApiResponse } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { Container, Row, Col, Form, Button, Badge } from 'react-bootstrap';
import SpinnerLoader from '../../components/Buttons/SpinnerLoader';

const CardBundleDetails = ({ batch: propBatch, cards: propCards, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { batchId } = useParams();
  const { currentEvent, user } = useAuthContext();

  const [batch, setBatch] = useState(propBatch || location.state?.batch || null);
  const [cards, setCards] = useState([]); // always start empty to fetch latest cards
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Payment & Resend states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [dynamicMnoOptions, setDynamicMnoOptions] = useState([]);
  const [resendTotalAmount, setResendTotalAmount] = useState('0');
  const [isSendingOrInitiating, setIsSendingOrInitiating] = useState(false);

  const [searchTerm, setSearchTerm] = useState(location.state?.searchKeyword || '');
  const [sortBy, setSortBy] = useState('name');

  const handleRefreshBatchCards = async () => {
    const pid = batch?.batchId || batchId;
    if (!pid) {
      toast.warning('Payment ID haipatikani.');
      return;
    }

    setIsRefreshing(true);
    try {
      const res = await ecardApi.refreshGeneratedCards({
        payment_id: pid,
        sync: true
      });
      const msg = res?.data?.output || res?.message || 'Uzalishaji wa kadi umerudiwa kikamilifu!';
      toast.success(msg);

      // Re-fetch batch details
      const batchRes = await ecardApi.getBatchDetails(pid);
      if (batchRes.success || batchRes.status === 800) {
        const batchData = Array.isArray(batchRes.data) ? batchRes.data[0] : batchRes.data;
        if (batchData) {
          setBatch(prev => ({
            ...prev,
            totalCards: Array.isArray(batchData.cards) ? batchData.cards.length : (typeof batchData.cards === 'number' ? batchData.cards : 0)
          }));
          setCards((batchData.cards || []).map(c => ({
            id: c.id,
            cardId: c.id,
            recipientName: c.name,
            recipientPhone: c.phone_number,
            cardNumber: c.card_number,
            cardType: String(c.card_type),
            templateImage: c.card_path,
            thumbnailImage: c.thumbnail_path,
            status: c.sent_status === 1 ? 'sent' : 'unsent'
          })));
        }
      }
    } catch (err) {
      console.error('Error refreshing batch cards:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'Failed to refresh eCard generation.';
      toast.error(errMsg);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (batchId) {
      setLoading(true);
      ecardApi.getBatchDetails(batchId)
        .then(res => {
          if (res.success || res.status === 800) {
            const batchData = Array.isArray(res.data) ? res.data[0] : res.data;
            if (batchData) {
              setBatch({
                batchId: batchData.payment_id || batchId,
                name: batchData.name || batchData.customer_fname || batch?.name || 'Card Batch',
                coupleName: batchData.customer_fname || batch?.coupleName || 'Card Batch',
                eventType: batchData.event_type || '',
                cardCategory: batchData.card_category || '',
                templateImage: batchData.preview_path || batch?.templateImage,
                isPaid: batchData.payment_status === 1 || batch?.isPaid,
                amount: batchData.amount || batch?.amount,
                totalCards: Array.isArray(batchData.cards) ? batchData.cards.length : (typeof batchData.cards === 'number' ? batchData.cards : 0)
              });
              setCards((batchData.cards || []).map(c => ({
                id: c.id,
                cardId: c.id,
                recipientName: c.name,
                recipientPhone: c.phone_number,
                cardNumber: c.card_number,
                cardType: String(c.card_type),
                templateImage: c.card_path,
                thumbnailImage: c.thumbnail_path,
                status: c.sent_status === 1 ? 'sent' : 'unsent'
              })));
            }
          }
        })
        .catch(err => {
          console.error(err);
          toast.error("Failed to load batch details");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [batchId]);

  /* ── Download via API ── */
  const handleDownload = async (card) => {
    if (!card.cardId) { toast.error('Card ID not found.'); return; }
    setDownloadingId(card.cardId);
    try {
      await ecardApi.downloadCard(
        card.cardId,
        `harusionline_Card_${card.recipientName || 'Guest'}_${card.cardType === '1' ? 'Single' : card.cardType === '2' ? 'Double' : ''}.png`
      );
      toast.success('Download started!');
    } catch (e) {
      console.error('Download error', e);
      toast.error('Download failed. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  /* ── Share ── */
  const handleShare = async (card) => {
    if (!card.cardId) { toast.error('Card ID not found.'); return; }

    const fallbackUrl = card.templateImage || batch.templateImage || '';
    const textMsg = `Check out this invitation for ${card.recipientName || 'Guest'}!\n\n${fallbackUrl}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Card for ${card.recipientName || 'Guest'}`,
          text: `Check out this invitation for ${card.recipientName || 'Guest'}!`,
          url: fallbackUrl || undefined,
        });
      } else {
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textMsg)}`, '_blank');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(textMsg)}`, '_blank');
      }
    }
  };

  const handleSendBatchCards = async (isResend = false) => {
    const unsent = cards.filter(c => c.status === 'unsent');

    if (!isResend && unsent.length > 0) {
      setIsSendingOrInitiating(true);
      try {
        toast.info(`Sending ${unsent.length} unsent card(s)...`);
        const response = await ecardApi.sendCard(batch.batchId);
        handleApiResponse(response);
      } catch (err) {
        console.error('Error sending cards:', err);
        handleApiResponse(err);
      } finally {
        setIsSendingOrInitiating(false);
      }
      return;
    }

    setIsSendingOrInitiating(true);
    try {
      toast.info('Fetching resend price & initiating payment...');

      let resendPrice = 250;
      try {
        const priceRes = await ecardApi.getResendPrice();
        const fetched = priceRes?.data?.resend_rate ||
          priceRes?.data?.resend_price ||
          priceRes?.data?.price ||
          priceRes?.data ||
          priceRes?.resend_rate;
        if (fetched && !isNaN(parseFloat(fetched))) {
          resendPrice = parseFloat(fetched);
        }
      } catch (pErr) {
        console.error('Could not fetch resend price, using default:', pErr);
      }

      const totalCardsCount = cards.length > 0 ? cards.length : (batch.totalCards || 1);
      const totalAmount = (totalCardsCount * resendPrice).toFixed(2);
      setResendTotalAmount(totalAmount);

      const paymentData = {
        service: "ecard_resend",
        amount: totalAmount,
        phone_number: user?.mobile || user?.phone_number || user?.phone || "0000000000",
        customer_name: currentEvent?.client_name || user?.name || 'Admin',
        template_id: batch?.batchId || batchId
      };

      const initiateResponse = await paymentApi.initiatePayment(paymentData);
      const paymentPayload = initiateResponse?.data || initiateResponse;

      if (initiateResponse?.success || initiateResponse?.status === 800 || initiateResponse?.status === 201 || paymentPayload?.payment) {
        const paymentId = paymentPayload?.payment?.id || paymentPayload?.id;
        const options = paymentPayload?.mno_options || [];

        if (!paymentId) throw new Error('Could not retrieve payment ID from initiation response.');

        setCurrentPaymentId(paymentId);
        setDynamicMnoOptions(options);
        setShowPaymentModal(true);
      } else {
        handleApiResponse(initiateResponse);
      }
    } catch (err) {
      console.error('Error initiating resend payment:', err);
      handleApiResponse(err);
    } finally {
      setIsSendingOrInitiating(false);
    }
  };

  const handleBack = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  if (loading) {
    return <SpinnerLoader />;
  }

  if (!batch) {
    return (
      <Container className="text-center py-5">
        <div className="bg-white p-5 rounded-4 shadow-sm border max-w-md mx-auto">
          <h4 className="fw-bold text-dark mb-3">No Batch Details Found</h4>
          <Button
            variant="primary"
            onClick={handleBack}
            className="rounded-pill px-4 py-2 fw-semibold shadow-sm border-0 d-inline-flex align-items-center gap-2"
            style={{ backgroundColor: '#4169e1' }}
          >
            <FaArrowLeft /> Rudi Nyuma
          </Button>
        </div>
      </Container>
    );
  }

  const isPaid = batch.isPaid || batch.paymentStatus?.toLowerCase() === 'paid';

  if (!isPaid) {
    return (
      <Container className="text-center py-5">
        <div style={{ maxWidth: '420px', margin: '40px auto', padding: '30px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <FaTimesCircle size={48} color="#ef4444" style={{ marginBottom: '16px' }} />
          <h4 style={{ fontWeight: '700', color: '#1e293b' }}>Payment Required</h4>
          <p className="text-muted mb-4">Please complete payment to access and view this batch of cards.</p>
          <Button
            onClick={handleBack}
            style={{
              padding: '10px 28px', background: '#4169e1',
              color: 'white', border: 'none', borderRadius: '30px',
              fontWeight: '600', cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', gap: '8px'
            }}
          >
            <FaArrowLeft /> Rudi Nyuma
          </Button>
        </div>
      </Container>
    );
  }

  const sentCards = cards.filter(c => c.status === 'sent');
  const unsentCards = cards.filter(c => c.status === 'unsent');

  const filteredCards = cards.filter(card => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return true;
    const matchesName = card.recipientName?.toLowerCase().includes(term);
    const matchesNumber = card.cardNumber?.toLowerCase().includes(term);
    const matchesPhone = card.recipientPhone?.includes(term);
    return matchesName || matchesNumber || matchesPhone;
  });

  const sortedCards = [...filteredCards].sort((a, b) => {
    if (sortBy === 'name') return a.recipientName.localeCompare(b.recipientName);
    if (sortBy === 'status') return a.status.localeCompare(b.status);
    if (sortBy === 'single') return (a.cardType === '1' ? -1 : 1) - (b.cardType === '1' ? -1 : 1);
    if (sortBy === 'double') return (a.cardType === '2' ? -1 : 1) - (b.cardType === '2' ? -1 : 1);
    return 0;
  });

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <Container className="py-4">
      <br></br>
      {/* ── Executive Top Navigation & Title Header ── */}
      <div className="bg-white p-3 p-md-4 rounded-4 shadow-sm border mb-4">
        <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3">
          {/* Back Button & Title */}
          <div className="d-flex align-items-center gap-3">
            <Button
              variant="light"
              onClick={handleBack}
              className="rounded-circle border p-2 d-flex align-items-center justify-content-center shadow-xs flex-shrink-0"
              style={{ width: '42px', height: '42px', backgroundColor: '#f8fafc', color: '#64748b' }}
              title="Rudi Nyuma"
            >
              <FaArrowLeft size={18} />
            </Button>
            <div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h3 className="fw-bold mb-0" style={{ color: '#1e293b', fontSize: '1.35rem' }}>
                  {batch.name || batch.coupleName}
                </h3>
                <Badge
                  bg={isPaid ? 'success-subtle' : 'danger-subtle'}
                  text={isPaid ? 'success' : 'danger'}
                  className="rounded-pill px-3 py-1.5 border d-inline-flex align-items-center gap-1"
                  style={{ fontSize: '0.75rem', fontWeight: '700' }}
                >
                  {isPaid ? <FaCheckCircle size={11} /> : <FaTimesCircle size={11} />}
                  {isPaid ? 'Paid Batch' : 'Unpaid Batch'}
                </Badge>
              </div>
            </div>
          </div>

          {/* Action Button: Send / Resend All */}
          {isPaid && (
            <Button
              variant={unsentCards.length > 0 ? "success" : "outline-primary"}
              onClick={() => handleSendBatchCards(unsentCards.length === 0)}
              disabled={isSendingOrInitiating}
              className="rounded-pill px-4 py-2.5 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2 border-0 flex-shrink-0"
              style={{
                backgroundColor: unsentCards.length > 0 ? '#10b981' : undefined,
                minHeight: '44px'
              }}
            >
              {isSendingOrInitiating ? (
                <><FaSpinner className="fa-spin" size={16} /> Inatuma Kadi...</>
              ) : (
                <><MdSend size={18} /> {unsentCards.length > 0 ? `Send Cards (${unsentCards.length})` : 'Resends / Tuma Tena'}</>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* ── Unpaid Alert Banner ── */}
      {!isPaid && (
        <div className="bg-danger-subtle border border-danger-subtle text-danger p-3 rounded-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2 shadow-xs">
          <div className="d-flex align-items-center gap-2">
            <FaTimesCircle size={20} className="text-danger flex-shrink-0" />
            <span className="fw-medium" style={{ fontSize: '0.9rem' }}>
              Kundi hili <strong>halijalipiwa</strong>. Kamilisha malipo ili kuwezesha kutuma kadi hizi kwa wageni.
            </span>
          </div>
          <Button
            variant="danger"
            size="sm"
            className="rounded-pill px-4 fw-semibold shadow-sm border-0"
            onClick={() => alert(`Inaandaa malipo ya kundi #${batch.batchId}`)}
          >
            Lipa Sasa
          </Button>
        </div>
      )}

      {/* ── Financial & Status Stat Cards Row ── */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <div className="bg-white p-3 rounded-4 shadow-sm border h-100 d-flex align-items-center gap-3">
            <div className="p-3 rounded-3 bg-primary-subtle text-primary d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '46px', height: '46px' }}>
              <FaList size={18} />
            </div>
            <div className="overflow-hidden">
              <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Jumla ya Kadi</div>
              <div className="fw-bold text-dark" style={{ fontSize: '1.25rem', lineHeight: '1.2' }}>
                {typeof batch?.totalCards === 'number' ? batch.totalCards : (Array.isArray(batch?.totalCards) ? batch.totalCards.length : cards.length)}
              </div>
            </div>
          </div>
        </Col>

        <Col xs={6} md={3}>
          <div className="bg-white p-3 rounded-4 shadow-sm border h-100 d-flex align-items-center gap-3">
            <div className="p-3 rounded-3 bg-success-subtle text-success d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '46px', height: '46px' }}>
              <FaCheckCircle size={18} />
            </div>
            <div className="overflow-hidden">
              <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Zilizotumwa</div>
              <div className="fw-bold text-success" style={{ fontSize: '1.25rem', lineHeight: '1.2' }}>{sentCards.length}</div>
            </div>
          </div>
        </Col>

        <Col xs={6} md={3}>
          <div className="bg-white p-3 rounded-4 shadow-sm border h-100 d-flex align-items-center gap-3">
            <div className="p-3 rounded-3 bg-warning-subtle text-warning d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '46px', height: '46px' }}>
              <FaTimesCircle size={18} />
            </div>
            <div className="overflow-hidden">
              <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Zisizotumwa</div>
              <div className="fw-bold text-warning" style={{ fontSize: '1.25rem', lineHeight: '1.2' }}>{unsentCards.length}</div>
            </div>
          </div>
        </Col>

        <Col xs={6} md={3}>
          <div className="bg-white p-3 rounded-4 shadow-sm border h-100 d-flex align-items-center gap-3">
            <div className="p-3 rounded-3 bg-info-subtle text-info d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '46px', height: '46px' }}>
              <FaCoins size={18} />
            </div>
            <div className="overflow-hidden">
              <div className="text-muted text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>Thamani (TZS)</div>
              <div className="fw-bold text-dark text-truncate" style={{ fontSize: '1.1rem', lineHeight: '1.2' }}>
                {(batch.amount || 0).toLocaleString()}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ── Search & Filter Toolbar ── */}
      <div className="bg-white p-3 rounded-4 shadow-sm border mb-4 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-3">
        <div className="position-relative flex-grow-1" style={{ maxWidth: '420px' }}>
          <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
          <Form.Control
            type="text"
            placeholder="Tafuta kwa jina au namba ya simu..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="ps-5 shadow-none border"
            style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0' }}
          />
        </div>

        <div className="d-flex align-items-center gap-2">
          <div className="position-relative">
            <FaSortAmountDown className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 5 }} />
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="ps-5 pe-4 shadow-none border"
              style={{ borderRadius: '25px', height: '42px', fontSize: '0.85rem', borderColor: '#e2e8f0', minWidth: '150px' }}
            >
              <option value="name">Panga kwa Jina</option>
              <option value="status">Panga kwa Hali</option>
              <option value="single">Single Cards</option>
              <option value="double">Double Cards</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {/* ── Cards Masonry Gallery Grid ── */}
      {sortedCards.length === 0 ? (
        <div className="bg-white rounded-4 shadow-sm border p-5 text-center my-4">
          <div className="mb-3 text-muted" style={{ fontSize: '2.5rem' }}>📭</div>
          <h5 className="fw-semibold text-secondary">Hakuna Kadi Zilizopatikana</h5>
          <p className="text-muted small mb-3">Jaribu kubadilisha neno la utafutaji au urekebishe kadi zilizolipwa zilizo na shida ya kuzalishwa.</p>
          {batch?.isPaid && (
            <Button
              variant="warning"
              className="rounded-pill px-4 py-2 fw-bold text-white d-inline-flex align-items-center gap-2 shadow-sm"
              style={{ backgroundColor: '#f59e0b', border: 'none' }}
              onClick={handleRefreshBatchCards}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <FaSpinner className="fa-spin" /> Inazalisha Kadi Zilizokosekana...
                </>
              ) : (
                <>
                  <FaSync /> Refresh Generated Cards (Kadi 0)
                </>
              )}
            </Button>
          )}
        </div>
      ) : (
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="my-masonry-grid"
          columnClassName="my-masonry-grid_column"
        >
          {sortedCards.map(card => (
            <div
              key={card.cardId}
              className="bg-white rounded-4 shadow-sm border overflow-hidden position-relative mb-4 d-flex flex-column transition-all"
              style={{ transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
            >
              {/* Sent/Unsent Badge */}
              <div
                className="position-absolute top-0 end-0 m-2.5 badge rounded-pill px-2.5 py-1.5 shadow-sm d-flex align-items-center gap-1"
                style={{
                  zIndex: 5,
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  backgroundColor: card.status === 'sent' ? '#10b981' : '#f59e0b',
                  color: '#ffffff'
                }}
              >
                {card.status === 'sent' ? <FaCheckCircle size={10} /> : <FaTimesCircle size={10} />}
                {card.status === 'sent' ? 'Sent' : 'Unsent'}
              </div>

              {/* Card Number & Type Badge */}
              <div
                className="position-absolute top-0 start-0 m-2.5 badge rounded-pill px-2.5 py-1.5 shadow-sm d-flex align-items-center gap-1"
                style={{ zIndex: 5, fontSize: '0.72rem', fontWeight: '600', backgroundColor: 'rgba(15, 23, 42, 0.75)', color: '#ffffff' }}
              >
                {card.cardNumber && <span>#{card.cardNumber}</span>}
                {card.cardType === '1' && <span className="opacity-75"> Single</span>}
                {card.cardType === '2' && <span className="opacity-75"> Double</span>}
              </div>

              {/* Card Image – Full uncut view */}
              <div className="overflow-hidden position-relative bg-light">
                <img
                  src={card.templateImage || card.thumbnailImage || batch.templateImage || 'https://via.placeholder.com/480x640?text=No+Preview'}
                  alt={card.recipientName}
                  className="w-100 d-block"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                  onError={e => {
                    if (e.target.src !== card.thumbnailImage && card.thumbnailImage) {
                      e.target.src = card.thumbnailImage;
                    } else {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/480x640?text=No+Preview';
                    }
                  }}
                />
              </div>

              {/* Recipient Details & Action Buttons Footer */}
              <div className="p-3 d-flex flex-column justify-content-between flex-grow-1 bg-white">
                <div className="mb-2">
                  <div className="fw-bold text-dark text-truncate d-flex align-items-center" style={{ fontSize: '0.9rem', color: '#1e293b', gap: '8px' }}>
                    <FaUser size={13} className="text-primary flex-shrink-0" style={{ opacity: 0.9 }} />
                    <span className="text-truncate" style={{ lineHeight: '1.2' }}>{card.recipientName}</span>
                  </div>
                  <div className="text-muted small d-flex align-items-center mt-1.5" style={{ fontSize: '0.82rem', color: '#64748b', gap: '8px' }}>
                    <FaPhone size={12} className="text-secondary flex-shrink-0" style={{ opacity: 0.7 }} />
                    <span style={{ letterSpacing: '0.4px' }}>{card.recipientPhone || 'N/A'}</span>
                  </div>
                </div>

                <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); handleDownload(card); }}
                    disabled={downloadingId === card.cardId}
                    className="btn btn-sm btn-link text-primary text-decoration-none p-0 d-flex align-items-center gap-1.5 fw-semibold"
                    style={{ fontSize: '0.82rem' }}
                  >
                    {downloadingId === card.cardId ? (
                      <><FaSpinner className="fa-spin" size={13} /> <span>Download...</span></>
                    ) : (
                      <><FaDownload size={13} /> <span>Download</span></>
                    )}
                  </button>

                  <span className="text-muted opacity-25" style={{ fontSize: '0.8rem' }}>|</span>

                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); handleShare(card); }}
                    className="btn btn-sm btn-link text-success text-decoration-none p-0 d-flex align-items-center gap-1.5 fw-semibold"
                    style={{ fontSize: '0.82rem' }}
                  >
                    <FaShare size={13} /> <span>Share</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </Masonry>
      )}

      {/* Resend Card Payment Modal */}
      <PaymentModal
        show={showPaymentModal}
        handleClose={() => setShowPaymentModal(false)}
        amount={resendTotalAmount}
        title="Malipo ya Kutuma Tena Kadi (Card Resend Payment)"
        paymentId={currentPaymentId}
        onPaymentSuccess={async () => {
          toast.success("Malipo ya kutuma tena kadi yamekamilika! Inatuma kadi sasa...");
          setShowPaymentModal(false);
          try {
            const sendRes = await ecardApi.sendCard(batch.batchId);
            handleApiResponse(sendRes);
          } catch (sendErr) {
            console.error('Error resending cards after payment:', sendErr);
            handleApiResponse(sendErr);
          }
        }}
        onPaymentError={(err) => {
          toast.error(err.message || "Imeshindwa kukamilisha malipo ya kutuma kadi");
        }}
      />
    </Container>
  );
};

export default CardBundleDetails;